import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import { BRAND } from '@/data/content';

export const Preloader = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    const o = { p: 0 };
    const tw = gsap.to(o, {
      p: 100,
      duration: 2.6,
      ease: 'power2.inOut',
      onUpdate: () => setProgress(Math.floor(o.p)),
      onComplete: () => setTimeout(() => setDone(true), 300),
    });
    return () => tw.kill();
  }, []);

  useEffect(() => {
    if (done && !fired.current) {
      fired.current = true;
      const t = setTimeout(onComplete, 250);
      return () => clearTimeout(t);
    }
  }, [done, onComplete]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          data-testid="preloader"
          className="fixed inset-0 z-[100] bg-void flex flex-col justify-between overflow-hidden"
          exit={{ y: '-100%' }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
        >
          <div className="flex-1 flex flex-col items-start justify-center pl-6 md:pl-24">
            <motion.img
              src="/assets/logo-mark.png"
              alt="Jupally Avenues"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="h-20 md:h-28 w-auto mb-8"
            />
            <div className="flex flex-wrap items-center gap-x-6 overflow-hidden">
              <div className="flex overflow-hidden">
                {'JUPALLY'.split('').map((c, i) => (
                  <motion.span
                    key={`j-${i}`}
                    initial={{ y: '110%' }}
                    animate={{ y: '0%' }}
                    transition={{ duration: 0.8, delay: 0.15 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    className="font-clash font-semibold text-5xl md:text-7xl tracking-tight text-[#1E3A7B]"
                  >
                    {c}
                  </motion.span>
                ))}
              </div>
              <div className="flex overflow-hidden">
                {'AVENUES'.split('').map((c, i) => (
                  <motion.span
                    key={`a-${i}`}
                    initial={{ y: '110%' }}
                    animate={{ y: '0%' }}
                    transition={{ duration: 0.8, delay: 0.15 + 7 * 0.05 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    className="font-clash font-semibold text-5xl md:text-7xl tracking-tight text-brand-gold"
                  >
                    {c}
                  </motion.span>
                ))}
              </div>
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-3 text-xs md:text-sm uppercase tracking-[0.35em] text-brand-gold"
            >
              {BRAND.tagline}
            </motion.p>
          </div>

          <div className="pb-10 px-6 md:px-24">
            <div className="flex items-end justify-between mb-4">
              <span className="text-[10px] uppercase tracking-[0.3em] text-ink/55">Avenues · Hyderabad</span>
              <span data-testid="preloader-progress" className="font-clash font-medium text-5xl md:text-7xl text-ink tabular-nums leading-none">
                {progress}<span className="text-brand-gold text-2xl md:text-4xl">%</span>
              </span>
            </div>
            <div className="h-px w-full bg-ink/10 relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-brand-gold"
                style={{ width: `${progress}%`, boxShadow: '0 0 18px 2px rgba(224,168,46,0.8)' }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
